import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import type { AnswerOut, FinePlacement } from '@/services/types';

/**
 * Where a company's fines sit within the corpus.
 *
 * The wording is deliberately careful. This is a PLACEMENT, not a finding: the
 * comparison population is the whole PADO corpus, not a set of comparable cases,
 * so a high percentile says "this company's fines are large relative to all
 * ANATEL fines" and emphatically NOT "this company is treated more harshly than
 * similar companies". The backend ships that caveat as a note on the answer so
 * it survives into an export; the panel renders it rather than paraphrasing.
 */
export function FinePlacementPanel({ answer }: { answer: AnswerOut<FinePlacement> }) {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });

  return (
    <section className="rounded-lg border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('anatel:entity.placement.title')}
      </h2>
      <Answer answer={answer} help={t('anatel:entity.placement.help')}>
        {(placement) => (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Figure
                label={t('anatel:entity.placement.companyMedian')}
                value={currency.format(placement.summary.quantiles.p50)}
                hint={t('anatel:entity.placement.overN', { n: placement.summary.n })}
              />
              <Figure
                label={t('anatel:entity.placement.corpusMedian')}
                value={currency.format(placement.corpus_median)}
                hint={t('anatel:entity.placement.overN', { n: placement.corpus_n })}
              />
              <Figure
                label={t('anatel:entity.placement.percentile')}
                value={percent.format(placement.median_percentile)}
                hint={t('anatel:entity.placement.ratio', {
                  ratio: number.format(placement.median_ratio),
                })}
              />
            </div>

            {/* The percentile as a position on a bar: a number alone invites
                reading 96% as a probability rather than a rank. */}
            <div
              className="h-2 w-full overflow-hidden rounded bg-muted"
              role="img"
              aria-label={t('anatel:entity.placement.percentile')}
            >
              <div
                className="h-full rounded bg-primary"
                style={{ width: `${Math.min(placement.median_percentile * 100, 100)}%` }}
              />
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-5">
              {Object.entries(placement.summary.quantiles).map(([label, value]) => (
                <div key={label}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="tabular-nums text-foreground">{currency.format(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </Answer>
    </section>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
